import styled from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import { usePrivy } from '@privy-io/react-auth';
import { hapticsImpactLight } from '../../common/haptic';
import ScanIcon from '../../assets/icons/scan.svg?react';
import SwapIcon from '../../assets/icons/swap.svg?react';
import SwapFilledIcon from '../../assets/icons/swap-filled.svg?react';
import HomeIcon from '../../assets/icons/home.svg?react';
import HomeFilledIcon from '../../assets/icons/home-filled.svg?react';
import GiftIcon from '../../assets/icons/gift.svg?react';
import RewardsIcon from '../../assets/icons/rewards.svg?react';
import RewardsFilledIcon from '../../assets/icons/rewards-filled.svg?react';
import CardIcon from '../../assets/icons/card.svg?react';
import CardFilledIcon from '../../assets/icons/card-filled.svg?react';

import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerAndroidScanningLibrary,
  CapacitorBarcodeScannerScanOrientation,
} from '@capacitor/barcode-scanner';
import { Html5QrcodeSupportedFormats } from '../../../node_modules/html5-qrcode/es2015/core';
import { Capacitor } from '@capacitor/core';
import { useGiftForm } from '../GiftFormContext';
import { selectTokenByNetworkAndSymbol } from '../../redux/slices/tokenSlice';
import { useFlag } from '@unleash/proxy-client-react';

const Container = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  position: fixed;
  bottom: 0px;
  left: 0px;
  right: 0px;
  width: 100vw;
  height: calc(56px + env(safe-area-inset-bottom));
  padding-left: 15px;
  padding-right: 15px;
  z-index: 3;
`;

const InnerContainer = styled.div`
  height: 48px;
  display: flex;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.background.secondary};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-top: 3px solid transparent;
  color: ${({ theme }) => theme.text.secondary};
  ${({ selected, theme }) =>
    selected &&
    `
      border-top: 3px solid ${theme.primary.primary};
      color: ${theme.primary.primary};
    `}
`;

const OptionText = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.text.regular};
  ${({ selected, theme }) => selected && `color: ${theme.primary.primary};`}
  ${({ middle, theme }) => middle && `color: ${theme.background.primary};`}
  font-weight: 600;
`;

const MobileNavigation = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = usePrivy();
  const enableCards = useFlag('enableCards');

  const homeSelected =
    location.pathname === '/app/home' ||
    (Capacitor.isNativePlatform() && location.pathname === '/');
  const rewardsSelected = location.pathname === '/rewards';
  const tradeSelected = location.pathname === '/app/trade';
  const { initializeForm } = useGiftForm();
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );
  const cardsSelected = location.pathname === '/app/cards';

  if (!isMobile || !user || location.pathname === '/update-required') {
    return;
  }

  return (
    <Container>
      <InnerContainer>
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/home');
          }}
          selected={homeSelected}
        >
          {homeSelected ? (
            <HomeFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <HomeIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={homeSelected}>Home</OptionText>
        </OptionContainer>
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            dispatch(
              openModal({
                name: 'selectToken',
                props: {
                  avoidClosing: true,
                  onContinue: (token) => {
                    initializeForm({
                      initialValues: {
                        targetTokenAddress: token.address,
                        network: token.network,
                      },
                    });
                    dispatch(
                      openModal({
                        name: 'giftFlow',
                      }),
                    );
                  },
                },
              }),
            );
          }}
        >
          <GiftIcon width={22} height={22} style={{ color: 'inherit' }} />
          <OptionText selected={false}>Gift</OptionText>
        </OptionContainer>
        {Capacitor.isNativePlatform() &&
          Capacitor.isPluginAvailable('CapacitorBarcodeScanner') && (
            <OptionContainer
              onClick={async () => {
                hapticsImpactLight();
                const { ScanResult, format } =
                  await CapacitorBarcodeScanner.scanBarcode({
                    hint: Html5QrcodeSupportedFormats.QR_CODE,
                    scanInstructions: 'Point at the QR',
                    scanOrientation:
                      CapacitorBarcodeScannerScanOrientation.PORTRAIT,
                    android: {
                      scanningLibrary:
                        CapacitorBarcodeScannerAndroidScanningLibrary.MLKIT,
                    },
                  });
                if (ScanResult && ScanResult.includes(`?gift=`)) {
                  const giftCode = ScanResult.split('?gift=')?.[1];
                  if (giftCode) {
                    navigate(`/?gift=${giftCode}`, { replace: true });
                  }
                }
                console.log('ScanResult', ScanResult);
              }}
              selected={false}
            >
              <ScanIcon width={22} height={22} style={{ color: 'inherit' }} />
              <OptionText selected={false}>Scan</OptionText>
            </OptionContainer>
          )}
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/trade');
          }}
          selected={tradeSelected}
        >
          {tradeSelected ? (
            <SwapFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <SwapIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={tradeSelected}>Trade</OptionText>
        </OptionContainer>
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/rewards');
          }}
          selected={rewardsSelected}
        >
          {rewardsSelected ? (
            <RewardsFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <RewardsIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={rewardsSelected}>Rewards</OptionText>
        </OptionContainer>
        {enableCards && (
          <OptionContainer
            onClick={() => {
              hapticsImpactLight();
              navigate('/app/cards');
            }}
            selected={cardsSelected}
          >
            {cardsSelected ? (
              <CardFilledIcon
                width={22}
                height={22}
                style={{ color: 'inherit' }}
              />
            ) : (
              <CardIcon width={22} height={22} style={{ color: 'inherit' }} />
            )}
            <OptionText selected={cardsSelected}>Cards</OptionText>
          </OptionContainer>
        )}
      </InnerContainer>
    </Container>
  );
};

export default MobileNavigation;
